import type {
  PhoneBook,
  PhoneCountry,
  PhoneNumbers,
  SplitPhone,
  TypedPhone,
} from '@/application/ports/PhoneBook';
import type { ReadPhone } from '@/domain';

/** The library, as much of it as is used: what `import('libphonenumber-js/max')` resolves to. */
type Library = typeof import('libphonenumber-js/max');
type CountryCode = import('libphonenumber-js/max').CountryCode;
type PhoneNumber = import('libphonenumber-js/max').PhoneNumber;

/** One way a country writes its numbers, as the library's metadata holds it. Not in its typings. */
interface NumberFormat {
  pattern(): string;
  leadingDigitsPatterns(): string[];
  internationalFormat(): string;
}

/**
 * Telephone numbers by libphonenumber, the library the server validates with
 * (libphonenumber-csharp there, libphonenumber-js here: one set of rules, two
 * ports of it). The request of 6 October 2026 (fifth), section 2.
 *
 * Imported dynamically, as the QR encoder is: only a form that takes a number
 * needs it, and it is far too large for the first page. The `max` build is
 * the one with the full metadata, so "valid" here means what it means on the
 * server; the default build only knows how long a number may be, and would
 * pass numbers the server then refuses.
 *
 * One load for the life of the page. The mock server validates with this same
 * class, so the mock, the form and the API all read a number alike.
 */
export class LibPhoneNumberBook implements PhoneBook {
  private loaded: Promise<PhoneNumbers> | null = null;

  load(): Promise<PhoneNumbers> {
    this.loaded ??= import('libphonenumber-js/max').then((library) => numbersOf(library));
    return this.loaded;
  }
}

/**
 * A number as people read one, as the SERVER writes it: libphonenumber's
 * INTERNATIONAL format, `+971 50 123 4567`, `+1 415-555-2671`.
 *
 * Not the library's own `formatInternational()`, which this port of the
 * library deliberately writes with spaces where the reference one keeps the
 * country's hyphens (`+1 415 555 2671`). The API formats with the reference
 * rules, and `phoneDisplay` is the API's to say, so the form's preview and the
 * mock server use them too: the country's own pattern for the number, applied
 * to the national number, after the plus and the calling code. A number no
 * pattern of its country fits is written as this port writes it.
 */
function displayOf(library: Library, number: PhoneNumber): string {
  const metadata = new library.Metadata();
  metadata.selectNumberingPlan(number.countryCallingCode as unknown as CountryCode);
  const plan = metadata.numberingPlan as unknown as { formats(): NumberFormat[] } | undefined;
  const national = number.nationalNumber;

  for (const format of plan?.formats() ?? []) {
    // The last of a format's leading digits is the most exact, and has to open the number.
    const leading = format.leadingDigitsPatterns().at(-1);
    if (leading !== undefined && national.search(new RegExp(leading)) !== 0) continue;
    if (!new RegExp(`^(?:${format.pattern()})$`).test(national)) continue;

    const written = format.internationalFormat();
    // A pattern marked as not for international use is no pattern for this.
    if (written === 'NA') continue;
    return `+${number.countryCallingCode} ${national.replace(new RegExp(format.pattern()), written)}`;
  }
  return number.formatInternational();
}

function numbersOf(library: Library): PhoneNumbers {
  const { AsYouType, getCountries, getCountryCallingCode, isSupportedCountry } = library;
  const parse = library.parsePhoneNumberFromString;

  /** The code as the library types it, or undefined for one it has no numbers for. */
  const known = (country: string): CountryCode | undefined => {
    const code = country.trim().toUpperCase();
    return isSupportedCountry(code) ? code : undefined;
  };

  const callingCode = (country: string): string | null => {
    const code = known(country);
    return code === undefined ? null : getCountryCallingCode(code);
  };

  const read = (candidate: string): ReadPhone | null => {
    // With no country assumed, so only a number that carries its own code reads.
    const number = parse(candidate);
    return number?.isValid() === true
      ? { e164: number.number, display: displayOf(library, number) }
      : null;
  };

  return {
    countries(): PhoneCountry[] {
      return getCountries().map((code) => ({ code, callingCode: getCountryCallingCode(code) }));
    },

    callingCode,

    typed(national: string, country: string): TypedPhone {
      const code = known(country);
      if (code === undefined) return { formatted: national, valid: false, e164: null, display: null };

      const formatted = new AsYouType(code).input(national);
      const number = parse(national, code);
      // Several countries share a calling code (+1, +44, +7), and a number of
      // any of them is a number under that code: the selector chose the code.
      const valid =
        number?.isValid() === true && number.countryCallingCode === getCountryCallingCode(code);

      return {
        formatted,
        valid,
        e164: valid ? number.number : null,
        display: valid ? displayOf(library, number) : null,
      };
    },

    read,

    split(text: string, assume?: string): SplitPhone | null {
      const fallback = assume === undefined ? undefined : known(assume);
      const number = fallback === undefined ? parse(text) : parse(text, fallback);
      if (number?.isValid() !== true) return null;

      /*
       * The country the number is in. A number of a shared calling code that
       * the library cannot place in one country (a toll free +1) is offered
       * under the assumed country when that shares the code, and otherwise
       * under the first country the code belongs to.
       */
      const country =
        number.country ??
        (fallback !== undefined && getCountryCallingCode(fallback) === number.countryCallingCode
          ? fallback
          : number.getPossibleCountries()[0]);
      return country === undefined ? null : { country, national: number.formatNational() };
    },
  };
}
